import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import Sidebar from "../../../layout/Sidebar";
import AdminDataTable from "../../../components/common/AdminDataTable";
import { testimonialsColumns } from "../../../tables/testimonialsColumns"; // Make sure you import columns properly
import { can } from "../../../utils/can";
import toast from "../../../utils/toast";

export default function ViewTestimonials() {
  const PAGE_SIZE = 10;

  const [testimonials, setTestimonials] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");

  // Fetch testimonials
  const fetchTestimonials = useCallback(async () => {
    try {
      setListLoading(true);

      const query = new URLSearchParams({
        page,
        pageSize,
        sortBy,
        sortOrder,
      });

      const res = await fetch(`${API_URL}/api/admin/testimonials-list?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await res.json();

      if (result.success) {
        setTestimonials(result.data || []);
        setTotalPages(Math.ceil(result.totalCount / pageSize));
      } else {
        setTestimonials([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Fetch Testimonials Error:", err);
      setTestimonials([]);
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, page, pageSize, sortBy, sortOrder]);

  // Auto fetch on dependency change
  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  // Delete handler
  const handleDelete = useCallback(async (id) => {
    if(can("delete_testimonials_and_success_stories","delete") === false){
      toast.showError("You don't have permission to delete testimonials.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this testimonial?")) return;

    try {
      setListLoading(true);
      const res = await fetch(`${API_URL}/api/admin/testimonials-list/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (result.success) {
        // Refresh list after deletion
        await fetchTestimonials();
        toast.showSuccess(result.message || 'Deleted successfully');
      } else {
        toast.showError(result.message || "Failed to delete");
      }
    } catch (err) {
      console.error("Delete Error:", err);
      toast.showError("Something went wrong");
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, fetchTestimonials]);

  const handleEdit = useCallback((row) => {
    if(can("edit_testimonials_and_success_stories","edit") === false){
      toast.showError("You don't have permission to edit testimonials.");
      return;
    }

    window.location.href = `/admin/testimonials/edit-testimonial/${row._id}`;
  }, []);

  const columns = useMemo(() => 
    testimonialsColumns({ handleEdit, handleDelete, PAGE_SIZE, API_URL }),
    [handleEdit, handleDelete, PAGE_SIZE, API_URL]
  );

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="View Testimonials & Success Stories" />

        <div className="right_col" role="main">
         

          <div className="x_panel">
            <div className="x_title">
              <h2>List</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>
              </ul>
              <div className="clearfix" />
            </div>

             <AdminDataTable
                  columns={columns}
                  data={testimonials}
                  page={page}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  loading={listLoading}
                  onPageChange={setPage}
                  onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                  }}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={(key) => {
                      if (sortBy === key) {
                          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                      } else {
                          setSortBy(key);
                          setSortOrder("asc");
                      }
                  }}
              />
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
